import React, {useEffect, useMemo, useState} from "react";

import {Button, Checkbox, Col, Modal, Pagination, Row, Space} from "antd";
import {EditOutlined, SearchOutlined, WarningOutlined} from '@ant-design/icons';
import {DiamondIcon, FlameIcon, GridIcon, KeyIcon, ListIcon} from "@components/icons/Icons";
import {IssueFiltersPaginatedQuery} from "@mwServices/Issue";
import {useGetWishlistIssuesQuery, useWishlistRemoveIssueMutation} from "@mwServices/User";
import {IssueGrid} from "@components/grid";
import {ClearFiltersBar} from "@components/issues-filter-drawer";
import {SortIcon} from "@components/sort-icon";
import {PAGINATION_PAGES} from "@components/grid/Grid";
import WishlistFiltersDrawer from "@mwPages/my-collection/wishlist/WishlistFiltersDrawer";
import WishlistGridItem from "@mwPages/my-collection/wishlist/WishlistGridItem";
import WishlistListItem from "@mwPages/my-collection/wishlist/WishlistListItem";
import {showResponse} from "@services/Api";
import usePreferences from "@hooks/usePreferences";
import {DebounceInput} from "@components/debounce-input";
import AddComicModal from "@components/add-comic-modal";
import {scrollToThen} from "@lib/utils";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {SCREEN, useWindowSize} from "@components/window-size-provider";

const WishlistIssues = ({search, setSearch, searchPlaceholder, searchCollection}) => {
    const windowDimensions = useWindowSize();
    const {showListView, setShowListView, itemCount, setItemCount} = usePreferences();
    const defaultFilters = useMemo(() => ({
        query: '',
        showVariants: true,
        series: undefined,
        multipleDateRanges: undefined,
        sort: 0,
    }), []);
    const [issueParams, setIssueParams] = useState<IssueFiltersPaginatedQuery>({
        filters: {...defaultFilters},
        page: 1,
        perPage: itemCount,
    });
    const issueQuery = useGetWishlistIssuesQuery(issueParams);
    const [wishlistIssueRemove, removeWishlistResponse] = useWishlistRemoveIssueMutation();
    const pagination = issueQuery && issueQuery.data && issueQuery.data.pagination ? issueQuery.data.pagination : null;
    const [showEditMultiple, setShowEditMultiple] = useState(false);
    const [selectedComicIds, setSelectedComicIds] = useState<number[]>([]);
    const [addToCollectionIds, setAddToCollectionIds] = useState<number[]>([]);
    const [showSearch, setShowSearch] = useState(false);

    const subscriptionType = useSubscriptionType();
    const showPriceInfo = useMemo(() => ( subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const showValueRibbon = useMemo(() => (subscriptionType.isStandard || subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);

    useEffect( () => {
        setIssueParams({...issueParams, page: 1, filters: {...issueParams.filters, query: search ? search : ''}});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[search]);

    useEffect(() => {
        showResponse(removeWishlistResponse, 'Issues removed from wishlist.', 'Failed to remove issues from wishlist.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeWishlistResponse]);

    useEffect(() => {
        setSelectedComicIds([]);
    }, [issueQuery.data])

    const showDesktopView = useMemo(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    const smallButtons = useMemo(() => {
        return windowDimensions.under(1200);
    }, [windowDimensions]);

    const toggleSelected = (id: number) => {
        setSelectedComicIds(currentIds => {
            if(currentIds.indexOf(id) === -1) {
                return [...currentIds, id];
            } else {
                return currentIds.filter((item) => item !== id);
            }
        })
    };

    return (
        <div className="mar-5">
            <Row className="flex-space-between">
                <Col>
                    <Space>
                        <Button icon={<GridIcon  />} size="large" className={"greyButton" + (showListView ? '' : ' selected')} onClick={() => setShowListView(false)} />
                        <Button icon={<ListIcon  />} size="large" className={"greyButton" + (showListView ? ' selected' : '')} onClick={() => setShowListView(true)} />
                        <Button className={"greyButton " + (showEditMultiple ? ' selectedBlue' : '')} size={"large"} onClick={() => setShowEditMultiple(!showEditMultiple)} >
                            <span className="filterButtonText">Edit</span>
                            <EditOutlined className="font-12" />
                        </Button>
                    </Space>
                </Col>
                <Col>
                    <Space>
                        {windowDimensions.up(SCREEN.MD) && (
                            <>
                                <Button
                                    icon={<div className="valueBadge hotIssue isActive mar-right-5"><FlameIcon /></div>}
                                    size="large"
                                    className={"greyButton " + (issueParams.filters.isHot ? ' selected' : '')}
                                    onClick={() => setIssueParams({
                                        ...issueParams,
                                        page: 1,
                                        filters: { ...issueParams.filters,
                                            isHot: !issueParams.filters.isHot,
                                            isKey: false,
                                            isRare: false
                                        }
                                    })}
                                >
                                    {issueQuery.data ? issueQuery.data.overview.is_hot : "0"}
                                </Button>
                                <Button
                                    icon={<div className="valueBadge keyIssue isActive mar-right-5"><KeyIcon /></div>}
                                    size="large"
                                    className={"greyButton " + (issueParams.filters.isKey ? ' selected' : '')}
                                    onClick={() => setIssueParams({
                                        ...issueParams,
                                        page: 1,
                                        filters: { ...issueParams.filters,
                                            isHot: false,
                                            isKey: !issueParams.filters.isKey,
                                            isRare: false
                                        }
                                    })}
                                >
                                    {issueQuery.data ? issueQuery.data.overview.is_key : "0"}
                                </Button>
                                <Button
                                    icon={<div className="valueBadge rareIssue isActive mar-right-5"><DiamondIcon /></div>}
                                    size="large"
                                    className={"greyButton " + (issueParams.filters.isRare ? ' selected' : '')}
                                    onClick={() => setIssueParams({
                                        ...issueParams,
                                        page: 1,
                                        filters: { ...issueParams.filters,
                                            isHot: false,
                                            isKey: false,
                                            isRare: !issueParams.filters.isRare,
                                        }
                                    })}
                                >
                                    {issueQuery.data ? issueQuery.data.overview.is_rare : "0"}
                                </Button>
                            </>
                        )}

                        {!windowDimensions.up(SCREEN.MD) && (
                            <Button className="greyButton " size="large" onClick={() => setShowSearch(!showSearch)}>
                                <SearchOutlined />
                            </Button>
                        )}

                        <WishlistFiltersDrawer
                            filters={issueParams.filters}
                            setFilters={(filters) => setIssueParams({...issueParams, page: 1, filters})}
                        />

                        {(windowDimensions.up(SCREEN.MD) && pagination) && (
                            <Pagination
                                current={pagination.current_page}
                                pageSize={pagination.per_page}
                                total={pagination.total_items}
                                onChange={(page) => {
                                    if(issueParams && issueParams.page !== page){
                                        setIssueParams({...issueParams, page: page});
                                    }
                                }}
                                simple
                            />
                        )}
                    </Space>
                </Col>
            </Row>

            {showSearch && (
                <Row className="mar-top-10" >
                    <DebounceInput
                        placeholder={searchPlaceholder}
                        className="searchInput"
                        prefix={<SearchOutlined />}
                        value={search}
                        maxLength={100}
                        debounceTimeout={2000}
                        onChange={(event) => {
                            const { value } = event.target;
                            if (setSearch && (value !== search)) setSearch(value);
                        }}
                    />
                </Row>
            )}

            {showEditMultiple && (
                <Row className={'edit-multiple-row ' + (windowDimensions.up(SCREEN.MD) ? 'large-screen ' : '')}>
                    <Col span={24} className="edit-multiple-col" >
                        <Checkbox
                            checked={issueQuery.data && selectedComicIds.length === issueQuery.data.issues.length}
                            onChange={() => {
                                if(issueQuery.data){
                                    if(selectedComicIds.length === issueQuery.data.issues.length){
                                        setSelectedComicIds([]);
                                    }else{
                                        setSelectedComicIds(issueQuery.data.issues.map(issue => issue.id) as number[]);
                                    }
                                }
                            }}
                        />

                        <Button
                            className="greyButton mar-top-5 edit-multi-item"
                            size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                            disabled={selectedComicIds.length === 0}
                            onClick={() => Modal.confirm({
                                autoFocusButton: null,
                                icon: <WarningOutlined />,
                                content: (<div>
                                    <div><b>Remove from Wishlist</b></div>
                                    <div className="pad-top-10" >
                                        Are you sure?<br/>
                                        Selecting this option will remove these issues from your wishlist.
                                    </div>
                                </div>),
                                onOk() {
                                    wishlistIssueRemove(selectedComicIds);
                                },
                            })}
                        >
                            Remove from Wishlist
                        </Button>

                        <Button
                            className="greyButton mar-top-5 edit-multi-item"
                            size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                            disabled={selectedComicIds.length === 0}
                            onClick={() => setAddToCollectionIds(selectedComicIds)}
                        >
                            Add to Collection
                        </Button>
                    </Col>
                </Row>
            )}

            <ClearFiltersBar
                filters={issueParams.filters}
                defaultFilters={defaultFilters}
                issueCount={issueQuery.data ? issueQuery.data.overview.total : 0}
                onClear={() => {
                    if(setSearch) setSearch('');
                    setIssueParams({...issueParams, page: 1, filters: defaultFilters});
                }}
            />

            {showListView ? (
                <>
                    {showDesktopView && (
                        <Row className="list-view-header">
                            {showEditMultiple && (
                                <Col span={1}>
                                </Col>
                            )}
                            <Col className={smallButtons ? "width-85px" : "width-135px"}>
                            </Col>
                            <Col className="pad-left-20 flex-grow-1">
                                <div className="flex">
                                    Title
                                    <SortIcon
                                        sort={issueParams.filters.sort}
                                        sortAsc={0}
                                        sortDesc={10}
                                        onChange={(sort) => setIssueParams({...issueParams,page: 1,  filters: {...issueParams.filters, sort}})}

                                    />
                                </div>
                            </Col>
                            <Col span={2} className="no-wrap">
                                <div className="flex-align-center">
                                    Qty
                                    <SortIcon
                                        sort={issueParams.filters.sort}
                                        sortAsc={21}
                                        sortDesc={22}
                                        onChange={(sort) => setIssueParams({...issueParams,page: 1,  filters: {...issueParams.filters, sort}})}

                                    />
                                </div>
                            </Col>
                            <Col span={3} className="no-wrap">
                                <div className="flex-align-center">
                                    Desired Grade
                                </div>
                            </Col>
                            <Col span={3} className="no-wrap">
                                <div className="flex-align-center">
                                    Est Value
                                    {showPriceInfo && (
                                        <SortIcon
                                            sort={issueParams.filters.sort}
                                            sortAsc={1}
                                            sortDesc={14}
                                            onChange={(sort) => setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, sort } })}

                                        />
                                    )}
                                </div>
                            </Col>
                            <Col span={2} className="no-wrap">
                                <div className="flex-align-center">
                                    Notes
                                </div>
                            </Col>
                            <Col span={2} className="no-wrap">
                                <div className="flex-align-center">Actions</div>
                            </Col>
                        </Row>
                    )}
                    <IssueGrid
                        isListGrid={true}
                        isLoading={issueQuery.isLoading || issueQuery.isFetching}
                        data={issueQuery.data}
                        noDataMessage="Use the + Add Button Above to Find and Add Comics to Your Wishlist"
                        IssueRender={(props) => (
                            <>
                                <WishlistListItem
                                    {...props}
                                    toggleSelected={toggleSelected}
                                    searchCollection={searchCollection}
                                    windowDimensions={windowDimensions}
                                />
                            </>)}
                        getItemProps={(item) => ({
                            key: item.id,
                            showEditMultiple,
                            isSelected: selectedComicIds.indexOf(item.id) !== -1,
                            showValueRibbon,
                            showDesktopView,
                        })}
                        paginationType={PAGINATION_PAGES}
                        onPaginationChange={(page => {
                            if(issueParams && issueParams.page !== page){
                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                    setIssueParams({ ...issueParams, page: page });
                                });
                            }
                        })}
                        onPaginationSizeChange={(current: number, perPage: number) => {
                            if(issueParams && issueParams.perPage !== perPage){
                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                    setIssueParams({
                                        ...issueParams,
                                        page: 1,
                                        perPage: perPage,
                                    });
                                    setItemCount(perPage);
                                });
                            }
                        }}
                    />
                </>
            ) : (
                <div className={'issue-grid-container ' + (showEditMultiple ? 'border-top ' : '')} >
                    <IssueGrid
                        isLoading={issueQuery.isLoading || issueQuery.isFetching}
                        data={issueQuery.data}
                        noDataMessage="Use the + Add Button Above to Find and Add Comics to Your Wishlist"
                        IssueRender={(props) => (
                            <>
                                <WishlistGridItem
                                    {...props}
                                    toggleSelected={toggleSelected}
                                    searchCollection={searchCollection}
                                    windowDimensions={windowDimensions}
                                />
                            </>)}
                        getItemProps={(item) => ({
                            key: item.id,
                            showEditMultiple,
                            isSelected: selectedComicIds.indexOf(item.id) !== -1,
                            showValueRibbon,
                            showDesktopView,
                        })}
                        paginationType={PAGINATION_PAGES}
                        onPaginationChange={(page => {
                            if(issueParams && issueParams.page !== page){
                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                    setIssueParams({ ...issueParams, page: page });
                                });
                            }
                        })}
                        onPaginationSizeChange={(current: number, perPage: number) => {
                            if (issueParams && issueParams.perPage !== perPage) {
                                scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                    setIssueParams({
                                        ...issueParams,
                                        page: 1,
                                        perPage: perPage,
                                    });
                                    setItemCount(perPage);
                                });
                            }
                        }}
                    />
                </div>
            )}

            {addToCollectionIds.length > 0 && (
                <AddComicModal isModalOpen={true} issueIds={addToCollectionIds} onCancel={() => setAddToCollectionIds([])} />
            )}
        </div>
    );
};

export default WishlistIssues;